import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
    Box,
    Card,
    CardHeader,
    CardContent,
    Typography,
    Button,
    CircularProgress,
    Alert,
    useTheme,
    useMediaQuery
} from '@mui/material';
import {
    AccountBalance as CongressIcon,
    OpenInNew as ViewAllIcon,
} from '@mui/icons-material';
import axios from '../../config/axiosConfig';
import ProfileImage from '../ProfileImage';
import { findBioguideId } from '../../utils/congressMembersMapping';

const MiniCongress = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch mini congress data
    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/congress/mini');
            setData(response.data);
            setError(null);
        } catch (error) {
            console.error('Error fetching mini congress data:', error);
            setError(error.response?.data?.error || 'Failed to fetch congress data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Format date to readable format
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    // Handle member name click
    const handleMemberClick = (memberName) => {
        navigate(`/congress-member/${encodeURIComponent(memberName)}`);
    };

    // Handle view all click
    const handleViewAll = () => {
        navigate('/congress-members');
    };

    // Loading state
    if (loading) {
        return (
            <Card sx={{ height: '100%', minHeight: 300 }}>
                <CardHeader
                    avatar={<CongressIcon />}
                    title="Congress Tracker"
                    titleTypographyProps={{
                        sx: {
                            fontSize: { xs: '1rem', sm: '1.25rem' },
                            fontWeight: 600
                        }
                    }}
                />
                <CardContent>
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                        <CircularProgress />
                    </Box>
                </CardContent>
            </Card>
        );
    }

    // Error state
    if (error) {
        return (
            <Card sx={{ height: '100%', minHeight: 300 }}>
                <CardHeader
                    avatar={<CongressIcon />}
                    title="Congress Tracker"
                    titleTypographyProps={{
                        sx: {
                            fontSize: { xs: '1rem', sm: '1.25rem' },
                            fontWeight: 600
                        }
                    }}
                />
                <CardContent>
                    <Alert severity="error">{error}</Alert>
                </CardContent>
            </Card>
        );
    }

    // Empty state
    if (!data?.latestBuys || data.latestBuys.length === 0) {
        return (
            <Card sx={{ height: '100%', minHeight: 300 }}>
                <CardHeader
                    avatar={<CongressIcon />}
                    title="Congress Tracker"
                    titleTypographyProps={{
                        sx: {
                            fontSize: { xs: '1rem', sm: '1.25rem' },
                            fontWeight: 600
                        }
                    }}
                    action={
                        <Button
                            size="small"
                            onClick={handleViewAll}
                            endIcon={<ViewAllIcon />}
                        >
                            View All
                        </Button>
                    }
                />
                <CardContent>
                    <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        minHeight="200px"
                        textAlign="center"
                    >
                        <CongressIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary">
                            No Recent Purchases
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            No recent congress trading activity available
                        </Typography>
                    </Box>
                </CardContent>
            </Card>
        );
    }

    // Active state with data
    return (
        <Card sx={{ height: '100%', minHeight: 300 }}>
            <CardHeader
                title="Congress Tracker"
                titleTypographyProps={{
                    onClick: () => navigate('/congress-tracker'),
                    sx: {
                        fontSize: { xs: '1rem', sm: '1.25rem' },
                        fontWeight: 600,
                        cursor: 'pointer',
                        '&:hover': {
                            color: 'primary.main',
                        },
                        transition: 'color 0.2s ease-in-out'
                    }
                }}
            />
            <CardContent sx={{ pt: 0 }}>
                <Box
                    sx={{
                        maxHeight: 280,
                        overflowY: 'auto',
                        overflowX: isMobile ? 'auto' : 'hidden',
                        '&::-webkit-scrollbar': {
                            width: '4px',
                        },
                        '&::-webkit-scrollbar-track': {
                            background: 'transparent',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            background: theme.palette.divider,
                            borderRadius: '4px',
                        },
                    }}
                >
                    {data.latestBuys.map((trade, index) => {
                        const bioguideId = findBioguideId(trade.name);

                        return (
                            <Box
                                key={trade.id}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: isMobile ? 1 : 1.5,
                                    py: isMobile ? 1 : 1.5,
                                    px: 0.5,
                                    borderBottom: index < data.latestBuys.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                                    minWidth: isMobile ? 'max-content' : 'auto',
                                    transition: 'background-color 0.2s ease-in-out',
                                    borderRadius: 1,
                                    '&:hover': {
                                        backgroundColor: theme.palette.action.hover,
                                    },
                                }}
                            >
                                {/* Profile Image */}
                                <ProfileImage
                                    name={trade.name}
                                    imageUrls={[
                                        bioguideId ? `/images/congress/${bioguideId}.jpg` : null
                                    ]}
                                    size={isMobile ? 24 : 32}
                                    sx={{ flexShrink: 0 }}
                                />

                                {/* Trade Information */}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    {/* Member Name - Clickable */}
                                    <Typography
                                        component="span"
                                        onClick={() => handleMemberClick(trade.name)}
                                        sx={{
                                            fontSize: isMobile ? '0.75rem' : '0.875rem',
                                            fontWeight: 600,
                                            color: theme.palette.primary.main,
                                            cursor: 'pointer',
                                            textDecoration: 'none',
                                            '&:hover': {
                                                textDecoration: 'underline',
                                            },
                                        }}
                                    >
                                        {trade.name}
                                    </Typography>

                                    {/* Symbol and Company */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                        <RouterLink
                                            to={`/asset/${trade.symbol}`}
                                            style={{
                                                color: theme.palette.primary.main,
                                                textDecoration: 'none',
                                                fontSize: isMobile ? '0.7rem' : '0.75rem',
                                                fontWeight: 600,
                                            }}
                                        >
                                            {trade.symbol}
                                        </RouterLink>

                                        <Typography sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem', color: 'text.secondary' }}>
                                            •
                                        </Typography>

                                        <Typography
                                            sx={{
                                                fontSize: isMobile ? '0.7rem' : '0.75rem',
                                                color: 'text.secondary',
                                                overflow: 'hidden',
                                                textOverflow: isMobile ? 'visible' : 'ellipsis',
                                                whiteSpace: isMobile ? 'normal' : 'nowrap',
                                                maxWidth: isMobile ? 'none' : '200px',
                                            }}
                                        >
                                            {trade.assetDescription}
                                        </Typography>
                                    </Box>

                                    {/* Amount and Date */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                        <Typography
                                            sx={{
                                                fontSize: isMobile ? '0.65rem' : '0.7rem',
                                                color: theme.palette.positive.main,
                                                fontWeight: 500,
                                            }}
                                        >
                                            {trade.amount}
                                        </Typography>

                                        <Typography sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', color: 'text.secondary' }}>
                                            •
                                        </Typography>

                                        <Typography
                                            sx={{
                                                fontSize: isMobile ? '0.65rem' : '0.7rem',
                                                color: 'text.secondary',
                                            }}
                                        >
                                            {formatDate(trade.date)}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            </CardContent>
        </Card>
    );
};

export default MiniCongress;